import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Home, Bed, Bath, Square, Calendar, CheckCircle2, Building2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export function PublicListingsPage() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND}/api/public/leasing/listings`);
        setListings(data.items || []);
      } catch (e) { if (typeof console !== 'undefined') console.debug('pages/public/PublicListingsPages.jsx: swallowed', e); }
      setLoading(false);
    })();
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="public-listings-page">
      <div className="border-b border-white/10 bg-card">
        <div className="max-w-6xl mx-auto px-6 py-6 flex items-center gap-3">
          <Building2 size={22} />
          <div>
            <div className="font-semibold text-lg">Prestigious Property Management</div>
            <div className="text-xs text-muted-foreground">Available units</div>
          </div>
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-6 py-10">
        {loading ? (
          <div className="text-center py-16 text-muted-foreground">Loading available units…</div>
        ) : listings.length === 0 ? (
          <Card className="p-12 border-white/10 bg-card text-center" data-testid="no-listings">
            <Home size={40} className="mx-auto opacity-30 mb-4" />
            <div className="text-lg font-medium">No units available right now</div>
            <div className="text-sm text-muted-foreground mt-2">Please check back soon.</div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {listings.map((l) => (
              <Card key={l.id} className="p-5 border-white/10 bg-card hover:border-white/30 transition cursor-pointer" onClick={() => nav(`/listings/${l.id}`)} data-testid={`public-listing-${l.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="text-xl font-semibold">Unit {l.unit}</div>
                  <div className="text-lg font-mono">${l.monthly_rent.toLocaleString()}<span className="text-xs text-muted-foreground">/mo</span></div>
                </div>
                <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                  <span className="flex items-center gap-1"><Bed size={12} /> {l.bedrooms} bd</span>
                  <span className="flex items-center gap-1"><Bath size={12} /> {l.bathrooms} ba</span>
                  {l.sqft && <span className="flex items-center gap-1"><Square size={12} /> {l.sqft} sqft</span>}
                </div>
                {l.description && <div className="text-sm text-muted-foreground line-clamp-2 mb-3">{l.description}</div>}
                <div className="flex items-center gap-2 text-xs">
                  <Calendar size={12} className="text-muted-foreground" />
                  <span className="text-muted-foreground">Available {l.available_date}</span>
                </div>
                {l.amenities?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {l.amenities.slice(0, 4).map((a, i) => <Badge key={i} variant="outline" className="text-[10px]">{a}</Badge>)}
                  </div>
                )}
                <Button size="sm" className="w-full mt-4" data-testid={`apply-btn-${l.id}`}>View & Apply</Button>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function PublicListingDetailPage() {
  const { lid } = useParams();
  const nav = useNavigate();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [form, setForm] = useState({
    applicant_name: "", applicant_email: "", applicant_phone: "",
    employer: "", monthly_income: "", credit_range: "excellent",
    desired_move_in: new Date().toISOString().slice(0, 10),
    references: "", notes: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND}/api/public/leasing/listings/${lid}`);
        setListing(data);
      } catch (e) {
        toast.error("Listing not available");
        nav("/listings");
      }
      setLoading(false);
    })();
  }, [lid, nav]);

  const submit = async () => {
    if (!form.applicant_name || !form.applicant_email || !form.applicant_phone || !form.monthly_income) {
      toast.error("Name, email, phone, and monthly income are required");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${BACKEND}/api/public/leasing/applications`, {
        listing_id: lid,
        ...form,
        monthly_income: parseFloat(form.monthly_income),
        pets: [], vehicles: [],
      });
      setSubmitted(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    }
    setSubmitting(false);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (!listing) return null;

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-foreground p-6" data-testid="application-submitted">
        <Card className="p-8 max-w-md text-center border-emerald-500/40 bg-emerald-500/5">
          <CheckCircle2 size={48} className="text-emerald-500 mx-auto mb-4" />
          <div className="text-xl font-semibold mb-2">Application submitted</div>
          <div className="text-sm text-muted-foreground mb-4">
            {submitted.message}
          </div>
          <div className="text-xs text-muted-foreground">
            Reference: <span className="font-mono" data-testid="app-ref">{submitted.application_ref}</span>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="public-listing-detail">
      <div className="border-b border-white/10 bg-card">
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-center gap-3">
          <Building2 size={22} />
          <div className="flex-1">
            <div className="font-semibold text-lg">Prestigious Property Management</div>
            {listing.property?.name && <div className="text-xs text-muted-foreground">{listing.property.name}</div>}
          </div>
          <Button variant="ghost" size="sm" onClick={() => nav("/listings")}>← All units</Button>
        </div>
      </div>
      <div className="max-w-4xl mx-auto px-6 py-8 grid md:grid-cols-2 gap-8">
        <div className="space-y-4">
          <div>
            <div className="text-3xl font-semibold mb-2">Unit {listing.unit}</div>
            <div className="text-2xl font-mono">${listing.monthly_rent.toLocaleString()}<span className="text-sm text-muted-foreground">/month</span></div>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <span className="flex items-center gap-1"><Bed size={14} /> {listing.bedrooms} bedrooms</span>
            <span className="flex items-center gap-1"><Bath size={14} /> {listing.bathrooms} bathrooms</span>
            {listing.sqft && <span className="flex items-center gap-1"><Square size={14} /> {listing.sqft} sqft</span>}
          </div>
          <div className="text-sm text-muted-foreground flex items-center gap-1">
            <Calendar size={14} /> Available {listing.available_date}
          </div>
          {listing.description && <p className="text-sm">{listing.description}</p>}
          {listing.amenities?.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Amenities</div>
              <div className="flex flex-wrap gap-1">
                {listing.amenities.map((a, i) => <Badge key={i} variant="outline">{a}</Badge>)}
              </div>
            </div>
          )}
        </div>
        <Card className="p-6 border-white/10 bg-card" data-testid="application-form">
          <div className="font-semibold text-lg mb-4">Apply for this unit</div>
          <div className="space-y-3">
            <div className="space-y-2"><Label>Full name *</Label>
              <Input value={form.applicant_name} onChange={(e) => setForm({ ...form, applicant_name: e.target.value })} data-testid="app-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Email *</Label>
                <Input type="email" value={form.applicant_email} onChange={(e) => setForm({ ...form, applicant_email: e.target.value })} data-testid="app-email" /></div>
              <div className="space-y-2"><Label>Phone *</Label>
                <Input value={form.applicant_phone} onChange={(e) => setForm({ ...form, applicant_phone: e.target.value })} data-testid="app-phone" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Employer</Label>
                <Input value={form.employer} onChange={(e) => setForm({ ...form, employer: e.target.value })} data-testid="app-employer" /></div>
              <div className="space-y-2"><Label>Monthly income *</Label>
                <Input type="number" value={form.monthly_income} onChange={(e) => setForm({ ...form, monthly_income: e.target.value })} data-testid="app-income" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Credit range</Label>
                <select className="w-full border border-white/10 bg-background rounded p-2 text-sm" value={form.credit_range}
                  onChange={(e) => setForm({ ...form, credit_range: e.target.value })} data-testid="app-credit">
                  <option value="excellent">Excellent (740+)</option>
                  <option value="good">Good (670-739)</option>
                  <option value="fair">Fair (580-669)</option>
                  <option value="poor">Poor (below 580)</option>
                </select>
              </div>
              <div className="space-y-2"><Label>Desired move-in</Label>
                <Input type="date" value={form.desired_move_in} onChange={(e) => setForm({ ...form, desired_move_in: e.target.value })} data-testid="app-move" /></div>
            </div>
            <div className="space-y-2"><Label>References (optional)</Label>
              <Textarea rows={2} placeholder="Previous landlord contacts" value={form.references} onChange={(e) => setForm({ ...form, references: e.target.value })} data-testid="app-refs" /></div>
            <div className="space-y-2"><Label>Anything else? (optional)</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="app-notes" /></div>
            <Button className="w-full" onClick={submit} disabled={submitting} data-testid="submit-app">
              {submitting ? "Submitting…" : "Submit application"}
            </Button>
            <div className="text-[10px] text-muted-foreground text-center">
              By submitting, you agree to a background/credit check as part of the screening process.
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
